/**
 * @architecture-header 1
 * @layer module
 * @domain reports
 * @responsibility Render the student recap with its timeline and practice suggestion.
 * @state-owner dataset store
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks
 * @forbidden teacher-only controls; direct API client
 * @public-api workspace main column
 * @side-effects navigation and toasts
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { Page, PageBody, PageHeader } from '../../../shared/components/PageShell';
import { Toolbar } from '../../../shared/components/VTable';
import { RUN_MODE_LABEL, STUDENT_TIMELINE } from '../../../shared/lib/demo-data';
import type { RunRecord } from '../../../shared/lib/model';
import { runName } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';

export function StudentRecap({ run }: { run: RunRecord }) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const notify = useWorkspaceUiStore((state) => state.notify);

  return (
    <Page maxWidth={900}>
      <PageHeader
        actions={
          <button className="bt" type="button" onClick={() => navigate('act', run.id)}>
            {t('看我的作答')}
          </button>
        }
        meta={`${classes[run.classIndex].name} · ${t(RUN_MODE_LABEL[run.mode])}`}
        title={`${t(run.dateLabel)} · ${runName(activities, run)}`}
      />
      <Toolbar
        left={
          <span className="vsumline">
            {t('這次 ')}
            <b>9 / 12</b> <em>·</em> 訂正後 <b>11 / 12</b> <em>·</em> AI 陪你 <b>2</b> 次 <em>·</em>{' '}
            老師來幫你 <b>1</b> 次
          </span>
        }
      />
      <PageBody>
        <div className="dgrid" style={{ margin: '16px 0 26px' }}>
          <div className="dcard">
            <h6>{t('這次成績')}</h6>
            <b className="big">9 / 12</b>
            <span>{t('訂正後 11 / 12')}</span>
          </div>
          <div className="dcard">
            <h6>AI 陪你</h6>
            <b className="big">2</b>
            <span>{t('次提示')}</span>
          </div>
          <div className="dcard">
            <h6>{t('老師來幫你')}</h6>
            <b className="big">1</b>
            <span>{t('次')}</span>
          </div>
        </div>
        <section className="panel">
          <header>
            <h2>{t('當時發生什麼')}</h2>
          </header>
          <div className="vtl2">
            {STUDENT_TIMELINE.map((entry) => (
              <div className={`vtl2r ${entry[1]}`} key={`${entry[0]}-${entry[2]}`}>
                <span className="vtt">{entry[0]}</span>
                {/** biome-ignore lint/security/noDangerouslySetInnerHtml: the fixture emphasises working steps with <b> */}
                <span className="vtxt" dangerouslySetInnerHTML={{ __html: t(entry[2]) }} />
              </div>
            ))}
          </div>
        </section>
        <section className="panel">
          <header>
            <h2>{t('你進步的地方')}</h2>
          </header>
          <div className="eqbox" style={{ fontSize: 16, lineHeight: '27px' }}>
            你這次自己用出了移項變號。上一次同樣的題型是老師講完你才會，這次是提示之後自己想通的。
          </div>
        </section>
        <section className="panel">
          <header>
            <h2>{t('下次要練什麼')}</h2>
          </header>
          <div className="hw">
            <span className="no5">1</span>
            <div>
              <b>{t('移項變號 同型題 6 題')}</b>
              <span>{t('只練這一個觀念，寫完 AI 會逐步核對')}</span>
            </div>
          </div>
          <button
            className="bt pri"
            style={{ marginTop: 14 }}
            type="button"
            onClick={() => notify(t('已加入我的作業'))}
          >
            {t('開始練習')}
          </button>
        </section>
      </PageBody>
    </Page>
  );
}
